import { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import DatePicker from 'react-datepicker';
import { FormGroup, Input } from 'src/components/ui';
import { t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import Icon from 'src/components/Icon';
import { isoToDate, dateToISO, getDateFnsLocale, getDateFormat } from './DateWidget';

import 'react-datepicker/dist/react-datepicker.css';
import { useTheme } from '@emotion/react';

type PeriodType = 'week' | 'month' | 'quarter' | 'custom';

function startOfQuarter(d: dayjs.Dayjs): dayjs.Dayjs {
  return d.month(Math.floor(d.month() / 3) * 3).startOf('month');
}

function endOfQuarter(d: dayjs.Dayjs): dayjs.Dayjs {
  return d.month(Math.floor(d.month() / 3) * 3 + 2).endOf('month');
}

function quarterNumber(d: dayjs.Dayjs): number {
  return Math.floor(d.month() / 3) + 1;
}

function computePeriod(type: Exclude<PeriodType, 'custom'>, anchor: dayjs.Dayjs): { start: dayjs.Dayjs; end: dayjs.Dayjs } {
  switch (type) {
    case 'week':
      return { start: anchor.startOf('week'), end: anchor.endOf('week') };
    case 'month':
      return { start: anchor.startOf('month'), end: anchor.endOf('month') };
    case 'quarter':
      return { start: startOfQuarter(anchor), end: endOfQuarter(anchor) };
  }
}

function formatPeriodLabel(type: Exclude<PeriodType, 'custom'>, anchor: dayjs.Dayjs): string {
  switch (type) {
    case 'week': {
      const start = anchor.startOf('week');
      const end = anchor.endOf('week');
      if (start.month() === end.month()) {
        return `${start.format('MMM D')}–${end.format('D, YYYY')}`;
      }
      return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
    }
    case 'month':
      return anchor.format('MMMM YYYY');
    case 'quarter':
      return `Q${quarterNumber(anchor)} ${anchor.year()}`;
  }
}

function navigateAnchor(type: Exclude<PeriodType, 'custom'>, anchor: dayjs.Dayjs, dir: 1 | -1): dayjs.Dayjs {
  switch (type) {
    case 'week':
      return anchor.add(dir, 'week');
    case 'month':
      return anchor.add(dir, 'month');
    case 'quarter':
      return anchor.add(dir * 3, 'month');
  }
}

interface Props {
  formData?: string;
  onChange: (value: string | undefined) => void;
  disabled?: boolean;
  readonly?: boolean;
  schema?: any;
  uiSchema?: any;
}

let _instanceCounter = 0;

export default function DatePeriodFilterField({ formData, onChange, disabled, readonly }: Props) {
  const theme = useTheme();
  const [instanceId] = useState(() => ++_instanceCounter);
  const fromPortalId = `datepicker-portal-period-from-${instanceId}`;
  const toPortalId = `datepicker-portal-period-to-${instanceId}`;

  const { i18n } = useLingui();
  const dateFormat = getDateFormat(i18n.locale);
  const locale = getDateFnsLocale(i18n.locale);

  const [periodType, setPeriodType] = useState<PeriodType | ''>('');
  const [anchor, setAnchor] = useState<dayjs.Dayjs>(dayjs());
  const [customFrom, setCustomFrom] = useState<Date | null>(null);
  const [customTo, setCustomTo] = useState<Date | null>(null);

  useEffect(() => {
    const fromEl = document.createElement('div');
    fromEl.id = fromPortalId;
    document.body.appendChild(fromEl);
    const toEl = document.createElement('div');
    toEl.id = toPortalId;
    document.body.appendChild(toEl);
    return () => {
      document.body.removeChild(fromEl);
      document.body.removeChild(toEl);
    };
  }, [fromPortalId, toPortalId]);

  const isCustom = periodType === 'custom';
  const active = !!periodType;

  useEffect(() => {
    if (!formData) {
      setPeriodType('');
      setAnchor(dayjs());
      setCustomFrom(null);
      setCustomTo(null);
    }
  }, [formData]);

  const label = useMemo(() => (periodType && !isCustom ? formatPeriodLabel(periodType as Exclude<PeriodType, 'custom'>, anchor) : ''), [
    periodType,
    anchor,
    isCustom,
  ]);

  function handleTypeChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const type = e.target.value as PeriodType | '';
    setPeriodType(type);
    if (type === 'custom') {
      setCustomFrom(null);
      setCustomTo(null);
      onChange(undefined);
    } else if (type) {
      const newAnchor = dayjs();
      setAnchor(newAnchor);
      const { start, end } = computePeriod(type, newAnchor);
      onChange(`${start.format('YYYY-MM-DD')}|${end.format('YYYY-MM-DD')}`);
    } else {
      onChange(undefined);
    }
  }

  function handleNavigate(dir: 1 | -1) {
    if (!periodType || isCustom) return;
    const newAnchor = navigateAnchor(periodType as Exclude<PeriodType, 'custom'>, anchor, dir);
    setAnchor(newAnchor);
    const { start, end } = computePeriod(periodType as Exclude<PeriodType, 'custom'>, newAnchor);
    onChange(`${start.format('YYYY-MM-DD')}|${end.format('YYYY-MM-DD')}`);
  }

  function handleCustomFromChange(date: Date | null) {
    setCustomFrom(date);
    if (date && customTo) onChange(`${dateToISO(date)}|${dateToISO(customTo)}`);
    else onChange(undefined);
  }

  function handleCustomToChange(date: Date | null) {
    setCustomTo(date);
    if (customFrom && date) onChange(`${dateToISO(customFrom)}|${dateToISO(date)}`);
    else onChange(undefined);
  }

  const isDisabled = !!(disabled || readonly);

  const divider = <span style={{ width: '1px', backgroundColor: '#ced4da', flexShrink: 0 }} />;

  const pickerProps = {
    locale,
    dateFormat,
    disabled: isDisabled,
    className: 'form-control form-control-sm border-0 rounded-0 w-full',
    wrapperClassName: 'block',
    showPopperArrow: false,
    popperProps: { strategy: 'fixed' as const },
    popperPlacement: 'bottom-start' as const,
    showYearDropdown: true,
    scrollableYearDropdown: true,
  };

  return (
    <FormGroup className="input-wrapper">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #ced4da',
          borderRadius: '0.375rem',
          overflow: 'hidden',
          borderColor: theme.gray400,
          height: '100%',
          minHeight: 0,
        }}
      >
        <Input
          type="select"
          bsSize="sm"
          value={periodType}
          onChange={handleTypeChange}
          disabled={isDisabled}
          className="date-picker-mode-select text-xs h-[38px] min-h-px "
          style={{
            maxWidth: 'max-content',
            flex: '0 0 auto',
            borderTopWidth: 0,
            borderBottomWidth: 0,
            borderLeftWidth: 0,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          }}
        >
          <option value="">{t`Date`}</option>
          <option value="week">{t`Week`}</option>
          <option value="month">{t`Month`}</option>
          <option value="quarter">{t`Quarter`}</option>
          <option value="custom">{t`From/To`}</option>
        </Input>
        {divider}
        {isCustom ? (
          <>
            <div style={{ flex: 1, minWidth: 0, height: 38 }}>
              <DatePicker
                {...pickerProps}
                selected={customFrom}
                onChange={handleCustomFromChange}
                placeholderText={t`From`}
                portalId={fromPortalId}
                className="date-picker-no-icon text-xs h-[38px]"
              />
            </div>
            {divider}
            <div style={{ flex: 1, minWidth: 0, height: 38 }}>
              <DatePicker
                {...pickerProps}
                selected={customTo}
                onChange={handleCustomToChange}
                placeholderText={t`To`}
                portalId={toPortalId}
                className="date-picker-no-icon text-xs h-[38px]"
              />
            </div>
          </>
        ) : (
          <>
            <button
              onClick={() => handleNavigate(-1)}
              disabled={!active || isDisabled}
              type="button"
              className="hover:bg-gray-100 disabled:opacity-40 disabled:cursor-default"
              style={{
                flex: '0 0 auto',
                alignSelf: 'stretch',
                border: 'none',
                background: 'transparent',
                borderRadius: 0,
                display: 'flex',
                alignItems: 'center',
                padding: '0 0.5rem',
                cursor: 'pointer',
                color: '#6c757d',
              }}
            >
              <Icon icon="fas fa-caret-left" size={14} />
            </button>
            {divider}
            <Input
              readOnly
              bsSize="sm"
              disabled={!active}
              value={label}
              placeholder="—"
              className="text-center"
              style={{ flex: 1, minWidth: 0, border: 'none', borderRadius: 0 }}
            />
            {divider}
            <button
              onClick={() => handleNavigate(1)}
              disabled={!active || isDisabled}
              type="button"
              className="hover:bg-gray-100 disabled:opacity-40 disabled:cursor-default"
              style={{
                flex: '0 0 auto',
                alignSelf: 'stretch',
                border: 'none',
                background: 'transparent',
                borderRadius: 0,
                display: 'flex',
                alignItems: 'center',
                padding: '0 0.5rem',
                cursor: 'pointer',
                color: '#6c757d',
              }}
            >
              <Icon icon="fas fa-caret-right" size={14} />
            </button>
          </>
        )}
      </div>
    </FormGroup>
  );
}
